import React, { useEffect } from 'react';
import cn from 'classnames';
import styles from './styles.module.css';

export type AccordionProps = {
  panels: AccordionPanelProps[],
  id: string,
  className?: string,
};

const Accordion: React.FC<AccordionProps> = ({ panels: initialPanels, id, className }) => {
  const [panelStates, setPanelStates] = React.useState<{ [key: string]: boolean }>({});

  React.useEffect(() => {
    setPanelStates(initialPanels.reduce((acc, panel) => {
      acc[panel.id] = panel.show;
      return acc;
    }, {} as { [key: string]: boolean }));
  }, [initialPanels]);

  const toggleAct = React.useCallback((selectedObj: AccordionPanelProps) => {
    setPanelStates(prevState => ({
      ...prevState,
      [selectedObj.id]: !prevState[selectedObj.id],
    }));
  }, []);

  return (
    <div id={id} className={className}>
      <div
        className={cn(styles.panelGroup, 'panel-group')}
        role="tablist"
        aria-multiselectable={true}
      >
        {initialPanels.map(panel => (
          <AccordionPanel
            {...panel}
            parentId={id}
            key={panel.id}
            show={panelStates[panel.id]}
            toggleAct={toggleAct}
          />
        ))}
      </div>
    </div>
  );
};

export type AccordionPanelProps = {
  id: string,
  parentId?: string,
  title: React.ReactNode,
  show: boolean,
  children: React.ReactNode,
  panelClassName?: string,
  toggleAct?: (obj: AccordionPanelProps) => void;
  leftIcon?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
  setToggleState?: (show: boolean) => void;
};

export const AccordionPanel: React.FC<AccordionPanelProps> = ({
  id,
  parentId = 'accordion',
  title,
  show,
  children,
  panelClassName,
  toggleAct,
  leftIcon,
  setToggleState = () => { },
}) => {
  const headerId = `${id}Header`;
  const panelId = `${id}Collapse`;
  const headerClass = show ? '' : 'collapsed';
  const panelClass = show ? 'in' : '';
  const [headerHover, setHeaderHover] = React.useState<boolean | undefined>();
  const ref = React.useRef<HTMLDivElement>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const getClassName = () => {
    const baseClass = 'sprites';
    const direction = show ? 'up' : 'down';
    const color = headerHover ? 'orange' : 'choc';
    const position = leftIcon ? 'left' : 'right';

    return `pull-${position} ${baseClass} sprite-arrow-${color}-${direction}`;
  }

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current); // Clear any pending timeouts
    }
    if (show && ref.current) {
      ref.current.style.maxHeight = `${ref.current.scrollHeight}px`;
      timeoutRef.current = setTimeout(() => {
        if (ref.current) {
          ref.current.style.overflow = 'visible'; // Hide overflow after transition
        }
      }, 500);
    } else if (!show && ref.current) {
      ref.current.style.maxHeight = '0';
      ref.current.style.overflow = 'hidden';
    }
  }, [show]);

  const handleClick = () => {
    setToggleState(!show);
    if (ref.current) {
      ref.current.style.maxHeight = `${ref.current.scrollHeight}px`;
    }
    if(toggleAct) {
      toggleAct({ id, parentId, title, show, children, panelClassName, toggleAct, leftIcon });
    }
  }

  return (
    <div className={cn('panel', styles.panel)}>
      <div
        className={cn('panel-heading', styles.panelHeading, leftIcon ? styles.leftSideIcon : styles.rightSideIcon)}
        id={headerId}
        role="tab"
        onClick={handleClick}
        onMouseEnter={() => setHeaderHover(true)}
        onMouseLeave={() => setHeaderHover(false)}
      >
        <h5 className={cn('panel-title')}>
          <span
            role="button"
            className={cn(headerClass)}
            data-toggle="collapse"
            data-parent={`#${parentId}`}
            aria-expanded={show}
            aria-controls={panelId}
            data-target={`#${panelId}`}
          >
            <i className={getClassName()}></i>
            {title}
          </span>
        </h5>
      </div>
      <div
        id={panelId}
        className={cn(
          'panel-collapse',
          panelClass,
          panelClassName,
        )}
        ref={ref}
        role="tabpanel"
        aria-labelledby={headerId}
      >
        <div className={cn('panel-body')}>{children}</div>
      </div>
    </div>
  );
};

export default Accordion;